import React, { useEffect, useState } from "react";
import { Container, Typography, CircularProgress, Button, Card, CardHeader, CardContent } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import api from "../../../services/api";
import { useAuth } from "../../../hooks/useAuth";
import { MainLogo } from "../../../components/MainLogo";
import { validationSchema, STATUS_OPTIONS, TIME_OPTIONS } from "../../../services/formConfig";
import PersonalInfoForm from "./PersonalInfoForm";
import AddressForm from "./AddressForm";
import InstitutionForm from "./InstitutionForm";
import PhotosForm from "./PhotosForm";
import OrderStatusForm from "./OrderStatusForm";

const AdminOrderEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [states, setStates] = useState([]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (!isAuthenticated()) return;
    setLoading(true);
    api
      .get(`/admin/orders/${id}`)
      .then((response) => setOrder(response.data))
      .catch((err) => {
        console.error("Erro ao buscar pedido:", err);
        setError("Erro ao carregar o pedido.");
      })
      .finally(() => setLoading(false));
  }, [id, isAuthenticated]);

  useEffect(() => {
    api
      .get("/states")
      .then((response) => {
        const data = Array.isArray(response.data) ? response.data : response.data.states || [];
        setStates(data);
      })
      .catch((err) => console.error("Erro ao carregar estados", err));
  }, []);

  const handleSubmit = (values, { setSubmitting }) => {
    console.log("handleSubmit chamado");
    const formData = new FormData();

    const fieldMap = {
      fullName: "full_name",
      cpf: "cpf",
      phoneNumber: "phone_number",
      birthDate: "birth_date",
      documentType: "document_type",
      documentNumber: "document_number",
      cep: "cep",
      stateId: "state_id",
      cityId: "city_id",
      streetName: "street_name",
      streetNumber: "street_number",
      streetComplement: "street_complement",
      neighborhood: "neighborhood",
      institutionName: "institution_name",
      customCourse: "custom_course",
      registrationNumber: "registration_number",
      courseType: "course_type",
      period: "period",
      time: "time",
      shelved: "shelved",
      designId: "design_id",
      status: "status",
    };

    Object.entries(values).forEach(([key, value]) => {
      if (key in fieldMap) {
        if (key === "registrationNumber" && values.registrationNotProvided) {
          formData.append(`order[${fieldMap[key]}]`, "");
        } else if (key === "shelved") {
          formData.append(`order[${fieldMap[key]}]`, value.toString());
        } else if (
          key !== "photoCard" &&
          key !== "photoDocumentFront" &&
          key !== "photoDocumentBack" &&
          key !== "photoInstitutionStatement"
        ) {
          formData.append(`order[${fieldMap[key]}]`, value);
        }
      }
    });

    if (values.photoCard instanceof File) {
      formData.append("order[card_photo]", values.photoCard);
    }
    if (values.photoDocumentFront instanceof File) {
      formData.append("order[card_document_photo_front]", values.photoDocumentFront);
    }
    if (values.photoDocumentBack instanceof File) {
      formData.append("order[card_document_photo_back]", values.photoDocumentBack);
    }
    if (values.photoInstitutionStatement instanceof File) {
      formData.append("order[card_institution_statement_photo]", values.photoInstitutionStatement);
    }

    console.log("Dados enviados:", Object.fromEntries(formData));

    api
      .patch(`/admin/orders/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        console.log("Resposta da API:", response);
        navigate(`/admin/orders/${id}`);
      })
      .catch((err) => {
        console.error("Erro ao atualizar pedido:", err);
        setError("Erro ao atualizar o pedido: " + (err.response?.data?.error || err.message));
      })
      .finally(() => setSubmitting(false));
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!order) return <Typography>Nenhum pedido encontrado.</Typography>;

  const validStatus = STATUS_OPTIONS.some((option) => option.value === order.status)
    ? order.status
    : "waiting_review";
  const validTime = TIME_OPTIONS.some((option) => option.value === order.time)
    ? order.time
    : "morning";

  return (
    <Container sx={{ padding: 3 }}>
      <MainLogo />
      <Typography variant="h4" textAlign="center" gutterBottom>
        Editar Pedido
      </Typography>
      <Formik
        enableReinitialize
        initialValues={{
          fullName: order.full_name || "",
          cpf: order.cpf || "",
          phoneNumber: order.phone_number || "",
          birthDate: order.birth_date ? formatDate(order.birth_date) : "",
          documentType: order.document_type ? order.document_type.toLowerCase() : "",
          documentNumber: order.document_number || "",
          cep: order.cep || "",
          stateId: order.state_id || "",
          cityId: order.city_id || "",
          streetName: order.street_name || "",
          streetNumber: order.street_number || "",
          streetComplement: order.street_complement || "",
          neighborhood: order.neighborhood || "",
          institutionName: order.institution_name || "",
          customCourse: order.custom_course || "",
          registrationNumber: order.registration_number || "",
          registrationNotProvided: !order.registration_number,
          courseType: order.course_type || "",
          period: order.period && !isNaN(order.period) ? Number(order.period) : 5,
          time: validTime,
          shelved: order.shelved || false,
          designId: order.design_id || "",
          photoCard: order.card_photo_url || "",
          photoDocumentFront: order.card_document_photo_front_url || "",
          photoDocumentBack: order.card_document_photo_back_url || "",
          photoInstitutionStatement: order.card_institution_statement_photo_url || "",
          status: validStatus,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, setFieldValue, isSubmitting, isValid, dirty }) => {
          console.log("Estado do botão - isValid:", isValid, "dirty:", dirty, "isSubmitting:", isSubmitting);
          return (
            <Form>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader title="Fotos do Pedido" sx={{ backgroundColor: "action.hover" }} />
                <CardContent>
                  <PhotosForm />
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader title="Dados Pessoais" sx={{ backgroundColor: "action.hover" }} />
                <CardContent>
                  <PersonalInfoForm values={values} errors={errors} touched={touched} handleChange={handleChange} />
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader title="Endereço" sx={{ backgroundColor: "action.hover" }} />
                <CardContent>
                  <AddressForm states={states} />
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader title="Instituição" sx={{ backgroundColor: "action.hover" }} />
                <CardContent>
                  <InstitutionForm />
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader title="Status do Pedido" sx={{ backgroundColor: "action.hover" }} />
                <CardContent>
                  <OrderStatusForm />
                </CardContent>
              </Card>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!(isValid && dirty) || isSubmitting}
              >
                Salvar Alterações
              </Button>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default AdminOrderEdit;